import React from 'react';
import { TextPairing } from '@components/TextPairing';
import { Flex } from '@components/Flex';
import { Box } from '@components/Box';
import { CTALink } from '@components/CTALink';
import { Column, Columns } from '@components/Columns';
import ReactHtmlParser from 'react-html-parser';
import { useAllTogetherNowSectionController } from '@sections/online-rsvp/AllTogetherNowSection/AllTogetherNowSection.controller';
import { Avatar, AvatarSizes } from '@components/Avatar';
import { JoykitColor } from '@components/themes';
import { Text } from '@components/typography';
import {
  AllTogetherTableContainer,
  AllTogetherTableHeaderWrapper,
  AnimationInnerContainer,
  AnimationOuterContainer,
  RowsContainer,
  Spacer1,
  Spacer2,
  Spacer3,
  StyledAllTogetherLastRowWrapper,
  StyledAllTogetherTableRow,
  StyledShadow,
  StyledTable,
  TableAvatar,
  TableDecorLeftImage,
  TableDecorRightImage,
  TableWrapper,
  HouseHoldTextPairing,
} from '@sections/online-rsvp/AllTogetherNowSection/AllTogetherNowSection.styles';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { CloudImage } from '@components/CloudImage';
import { useTranslation } from 'react-i18next';

interface TextCellProps {
  style: React.CSSProperties;
  color?: JoykitColor;
}

const HeaderTextCell: React.FC<React.PropsWithChildren<TextCellProps>> = ({ children, style, color = 'blue5' }) => {
  return (
    <Text color={color} style={style} tagName="p" variant="featureHed17">
      {children}
    </Text>
  );
};

export const TextCell: React.FC<React.PropsWithChildren<TextCellProps>> = ({ children, style, color = 'blue6' }) => {
  return (
    <Text color={color} style={style} tagName="p" variant="featureCopy17">
      {children}
    </Text>
  );
};

export const getCheckMarkImage = (isChecked?: boolean) => {
  return isChecked !== undefined && <CloudImage src={`common/icons/${isChecked ? 'yes' : 'no'}.svg`} alt="" />;
};

export const AllTogetherTableHeader: React.FC<{}> = () => {
  const { t } = useTranslation('onlineRsvp');
  const header = t('allTogetherTable.header');

  return (
    <AllTogetherTableHeaderWrapper flexDirection="row" backgroundColor="mono7">
      <Avatar sizeVariant={AvatarSizes.sm} strokeWidth="3px" strokeColor="blue3" style={{ marginRight: '32px', visibility: 'hidden' }} />
      <HeaderTextCell style={{ flex: 1.4 }}>{header.guest}</HeaderTextCell>
      <HeaderTextCell style={{ flex: 1 }}>{header.rehearsal}</HeaderTextCell>
      <HeaderTextCell style={{ flex: 1 }}>{header.ceremony}</HeaderTextCell>
      <HeaderTextCell style={{ flex: 1 }}>{header.reception}</HeaderTextCell>
      <HeaderTextCell style={{ flex: 1 }}>{header.brunch}</HeaderTextCell>
    </AllTogetherTableHeaderWrapper>
  );
};

export const AllTogetherSection: React.FC<{}> = () => {
  const sectionId = 'Multi-Event_RSVP';
  const { sectionRef, tableWrapperRef, tableRef, partyAnimationContainerRef, partyAnimationElRef, initAssets } =
    useAllTogetherNowSectionController();

  const { t } = useTranslation('onlineRsvp');
  const allTogetherNowData = t('allTogetherNow');
  const rows = t('allTogetherTable.rows');

  return (
    <Box
      as="section"
      className="all-together-section-desktop"
      ref={sectionRef}
      paddingTop={13}
      paddingBottom={[8, 15, 16, 16, 13]}
      backgroundColor="yellow1"
      id={sectionId}
    >
      <LazyLoadWrapper beforeLoad={initAssets}>
        <div />
      </LazyLoadWrapper>
      <Columns>
        <Column width={12}>
          <Flex flexDirection={'column'} alignItems={'center'}>
            <TextPairing
              marginBottom={8}
              alignment="center"
              eyebrowColor="blue5"
              eyebrowVariant={'calloutHed32'}
              eyebrow={allTogetherNowData.unified.eyebrow}
              eyebrowTagName="h2"
              titleColor="blue6"
              titleVariant={'hed120Super'}
              title={allTogetherNowData.unified.title}
              titleTagName="h2"
              bodyColor="blue6"
              bodyVariant="dek24"
              body={allTogetherNowData.unified.content}
              bodyMaxWidth={'805px'}
            />
            <CTALink id="allTogetherNowToGuestList" href="/guest-list" typography={'featureHed20'} hasChevron>
              Explore the Connected Guest List
            </CTALink>
          </Flex>
        </Column>
      </Columns>
      <div ref={tableWrapperRef}>
        <Columns>
          <Column width={12}>
            <Flex flexDirection={'column'} alignItems={'center'}>
              <AllTogetherTableContainer flexDirection="column">
                <TableWrapper ref={tableRef}>
                  <TableDecorLeftImage src="wedding-invitations/unified-rsvp/left.png" />
                  <StyledTable>
                    <AllTogetherTableHeader />
                    <RowsContainer>
                      {rows.map((row, index) => {
                        const isLastRow = index === rows.length - 1;
                        const rowUi = (
                          <StyledAllTogetherTableRow key={index} style={{ zIndex: index === 3 ? 1 : 'unset' }}>
                            <TableAvatar
                              sizeVariant={AvatarSizes.sm}
                              strokeWidth="3px"
                              strokeColor="blue3"
                              isHidden={index === 3 || index === 4}
                              src={row.src}
                            />
                            {index === 3 && (
                              <AnimationOuterContainer ref={partyAnimationContainerRef}>
                                <AnimationInnerContainer>
                                  <div ref={partyAnimationElRef} />
                                </AnimationInnerContainer>
                              </AnimationOuterContainer>
                            )}
                            <TextCell style={{ flex: 1.4 }}>{row.name}</TextCell>
                            <div style={{ flex: 1 }}>{getCheckMarkImage(row.rehearsal)}</div>
                            <div style={{ flex: 1 }}>{getCheckMarkImage(row.ceremony)}</div>
                            <div style={{ flex: 1 }}>{getCheckMarkImage(row.reception)}</div>
                            <div style={{ flex: 1 }}>{getCheckMarkImage(row.brunch)}</div>
                          </StyledAllTogetherTableRow>
                        );
                        return isLastRow ? <StyledAllTogetherLastRowWrapper key={index}>{rowUi}</StyledAllTogetherLastRowWrapper> : rowUi;
                      })}
                    </RowsContainer>
                  </StyledTable>
                  <StyledShadow />
                  <TableDecorRightImage src="wedding-invitations/unified-rsvp/right.png" isDecor />
                </TableWrapper>
              </AllTogetherTableContainer>
            </Flex>
          </Column>
        </Columns>
      </div>
      <Spacer1 />
      <Columns>
        <Column width={6} offset={6}>
          <Box id="Easily_Group_Your_Guests">
            <TextPairing
              id="all-together-first-text-block"
              eyebrowColor="blue5"
              eyebrowVariant={'sectionEyeBrow24'}
              eyebrow={allTogetherNowData.households.eyebrow}
              titleColor="blue6"
              titleVariant={'hed72Hero'}
              title={allTogetherNowData.households.title}
              titleTagName="h3"
              bodyColor="blue6"
              bodyVariant="dek24"
              body={allTogetherNowData.households.content}
              titleMaxWidth={'330px'}
              bodyMaxWidth={'438px'}
            />
          </Box>
        </Column>
      </Columns>
      <Spacer2 />
      <Columns>
        <Column width={6}>
          <HouseHoldTextPairing
            id="all-together-second-text-block"
            eyebrowTagName="h2"
            eyebrowColor="blue5"
            eyebrowVariant={'sectionEyeBrow24'}
            eyebrow={allTogetherNowData.rsvpGroups.eyebrow}
            titleColor="blue6"
            titleVariant={'hed56'}
            title={ReactHtmlParser(allTogetherNowData.rsvpGroups.title)}
            titleTagName="h3"
            bodyColor="blue6"
            bodyVariant="dek20"
            body={allTogetherNowData.rsvpGroups.content}
            bodyMaxWidth={'338px'}
          />
        </Column>
      </Columns>
      <Spacer3 />
    </Box>
  );
};

export default AllTogetherSection;
